var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { getDriveActionLabelKey, SHARING_STATUS_LABEL_KEYS } from '@web/components/Apps/DriveAdvancedSearch/utils';
import { formatPersonWithOrgUnit } from '@web/components/Apps/SearchMeetings/utils';
import Loading from '@web/components/layout/Loading';
import PaginatedTable from '@web/components/layout/PaginatedTable/PaginatedTable';
import { OpenInNewTabIcon } from '@web/components/ui/icons/OpenInNewTabIcon';
import { Tooltip } from '@web/components/ui/Tooltip';
import { itemDetailsUrl } from '@web/router/urls';
import { getDriveLinkByType, getIconByType } from '@web/utility';
import { useNavigateTo } from '@web/utility/hooks/useNavigateTo';
import { ApplicationEnum } from '@workalizer/shared';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
var formatActivityDate = function (date) {
    return format(new Date(date), 'd MMM HH:mm');
};
var ActivityCell = function (_a) {
    var activity = _a.activity, _b = _a.showAction, showAction = _b === void 0 ? false : _b, externalLabel = _a.externalLabel;
    var t = useTranslation().t;
    if (!activity) {
        return _jsx("span", __assign({ className: "text-sm text-gray-500" }, { children: "\u2014" }));
    }
    var personLabel = formatPersonWithOrgUnit({
        name: activity.user.name,
        email: activity.user.email,
        orgUnitTitle: activity.user.orgUnitTitle,
        externalLabel: externalLabel
    });
    return (_jsxs("div", __assign({ className: "text-sm text-gray-700" }, { children: [_jsx("div", { children: showAction
                    ? "".concat(personLabel, ": ").concat(t(getDriveActionLabelKey(activity.action, activity.subAction)))
                    : personLabel }), _jsx("div", __assign({ className: "text-gray-500" }, { children: formatActivityDate(activity.date) }))] })));
};
export var DocumentsTable = function (_a) {
    var data = _a.data, total = _a.total, page = _a.page, pageSize = _a.pageSize, isLoading = _a.isLoading;
    var t = useTranslation().t;
    var navigateTo = useNavigateTo();
    var externalLabel = t('app.apps.searchMeetings.table.externalGuest');
    if (isLoading) {
        return _jsx(Loading, {});
    }
    var columns = [
        {
            title: t('app.driveAdvancedSearch.table.document'),
            isVisible: true,
            className: 'sm:px-3'
        },
        {
            title: t('app.driveAdvancedSearch.table.owner'),
            isVisible: true,
            className: 'sm:px-3'
        },
        {
            title: t('app.driveAdvancedSearch.table.lastViewed'),
            isVisible: true,
            className: 'sm:px-3'
        },
        {
            title: t('app.driveAdvancedSearch.table.lastActivity'),
            isVisible: true,
            className: 'sm:px-3'
        },
        {
            title: t('app.driveAdvancedSearch.table.sharing'),
            isVisible: true,
            className: 'sm:px-3'
        }
    ];
    var tableData = data.map(function (doc) { return [
        {
            id: 'document',
            render: function () { return (_jsxs("div", __assign({ className: "flex items-start gap-2 text-gray-700 min-w-0" }, { children: [_jsx("span", __assign({ className: "shrink-0 mt-0.5" }, { children: getIconByType(doc.type, { width: '15', height: '15' }) })), _jsx(Tooltip, __assign({ content: t('app.driveAdvancedSearch.openDocDetails') }, { children: _jsx("button", __assign({ type: "button", className: "min-w-0 flex-1 text-sm leading-5 font-normal text-gray-900 underline decoration-dashed decoration-indigo-500 underline-offset-2 whitespace-normal break-words text-left cursor-pointer hover:text-indigo-600 hover:decoration-indigo-600", onClick: function () {
                                return navigateTo({
                                    pathname: itemDetailsUrl,
                                    newFilters: {
                                        itemId: doc.id,
                                        app: ApplicationEnum.DRIVE,
                                        page: page
                                    }
                                });
                            } }, { children: doc.title })) })), _jsx(Link, __assign({ to: getDriveLinkByType(doc.type, doc.slugId), target: "_blank", className: "shrink-0 mt-0.5", onClick: function (event) { return event.stopPropagation(); } }, { children: _jsx(OpenInNewTabIcon, { width: "16", height: "16" }) }))] }))); }
        },
        {
            id: 'owner',
            render: function () { return (_jsx("div", __assign({ className: "text-sm text-gray-700" }, { children: doc.owner
                    ? formatPersonWithOrgUnit({
                        name: doc.owner.name,
                        email: doc.owner.email,
                        orgUnitTitle: doc.owner.orgUnitTitle,
                        externalLabel: externalLabel
                    })
                    : '—' }))); }
        },
        {
            id: 'lastViewed',
            render: function () { return (_jsx(ActivityCell, { activity: doc.lastViewed, externalLabel: externalLabel })); }
        },
        {
            id: 'lastActivity',
            render: function () { return (_jsx(ActivityCell, { activity: doc.lastActivity, showAction: true, externalLabel: externalLabel })); }
        },
        {
            id: 'sharing',
            render: function () { return (_jsx("div", __assign({ className: "text-sm text-gray-700" }, { children: doc.sharing.length > 0
                    ? doc.sharing
                        .map(function (status) { return t(SHARING_STATUS_LABEL_KEYS[status]); })
                        .join(' / ')
                    : '—' }))); }
        }
    ]; });
    return (_jsx(PaginatedTable, { columns: columns, data: tableData, total: total, typeResultText: "documents", pageSize: pageSize, title: t('app.driveAdvancedSearch.searchResults', {
            count: total
        }) }));
};
